import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    delay: { type: Number, default: 300 },
    preview: { type: Boolean, default: false },
    minLength: { type: Number, default: 3 }
  }

  static targets = []

  connect() {
    this.submitTimeout = null
    this.queuedSubmit = false
    if (this.previewValue) {
      this.boundClickOutside = this.clickOutside.bind(this)
      document.addEventListener("click", this.boundClickOutside)
      if (this.inputIsBlank()) this.hidePreview()
    }
  }

  disconnect() {
    this.clearPending()
    if (this.boundClickOutside) {
      document.removeEventListener("click", this.boundClickOutside)
    }
  }

  showOnFocus() {
    if (this.inputIsBlank() || this.inputIsTooShort()) return

    const frame = this.frameElement
    const hasContent = frame && frame.innerHTML.trim()

    if (this.previewValue) {
      hasContent ? this.showPreview() : this.submitNow()
    } else if (!hasContent) {
      this.submitNow()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.previewValue) {
      this.hidePreview()
      event.target.blur()
    }
  }

  clickOutside(event) {
    const container = this.previewContainer
    if (this.element.contains(event.target)) return
    if (container && container.contains(event.target)) return
    this.hidePreview()
  }

  queueSubmit() {
    this.clearPending()

    if (this.previewValue && this.inputIsBlank()) {
      this.clearPreview()
      return
    }

    if (this.inputIsTooShort()) {
      if (this.previewValue) this.clearPreview()
      return
    }

    if (this.previewValue) {
      this.showPreview()
      this.showLoading()
    }

    this.submitTimeout = setTimeout(() => {
      this.queuedSubmit = true
      this.element.requestSubmit()
    }, this.delayValue)
  }

  clearPending(event) {
    const queuedSubmit = this.queuedSubmit
    this.queuedSubmit = false

    if (event && this.previewValue && (this.inputIsBlank() || this.inputIsTooShort())) {
      event.preventDefault()
      this.clearPreview()
    }

    if (event && !event.defaultPrevented && this.inputIsTooShort()) {
      event.preventDefault()
      return
    }

    if (event && !queuedSubmit && this.frameId && !event.defaultPrevented) {
      event.preventDefault()
      window.location.assign(this.fullPageSearchUrl())
    }

    if (!this.submitTimeout) return

    clearTimeout(this.submitTimeout)
    this.submitTimeout = null
  }

  handleFrameLoad(event) {
    if (!this.previewValue) return
    if (event.target.id !== this.frameId) return

    this.hideLoading()

    if (this.inputIsBlank()) {
      this.clearPreview()
      return
    }

    this.showPreview()
  }

  clearPreview() {
    const frame = this.frameElement
    if (frame) frame.innerHTML = ""
    this.hidePreview()
  }

  hidePreview() {
    const container = this.previewContainer
    if (!container) return

    container.classList.add("hidden")
  }

  showPreview() {
    const container = this.previewContainer
    if (!container) return

    container.classList.remove("hidden")
  }

  inputIsBlank() {
    const input = this.element.querySelector('input[name="q"]')
    return !input || input.value.trim() === ""
  }

  inputIsTooShort() {
    const input = this.element.querySelector('input[name="q"]')
    return !input || input.value.trim().length < this.minLengthValue
  }

  get frameId() {
    return this.element.dataset.turboFrame
  }

  get frameElement() {
    if (!this.frameId) return null
    return document.getElementById(this.frameId)
  }

  get previewContainer() {
    if (!this.frameId) return null
    return document.getElementById(`${this.frameId}_wrapper`) || document.getElementById(this.frameId)
  }

  submitNow() {
    if (this.previewValue) this.showPreview()
    this.showLoading()
    this.queuedSubmit = true
    this.element.requestSubmit()
  }

  fullPageSearchUrl() {
    const formData = new FormData(this.element)
    const query = new URLSearchParams(formData).toString()
    const action = this.element.getAttribute("action") || window.location.pathname
    const url = new URL(action, window.location.origin)

    url.search = query
    return url.toString()
  }

  showLoading() {
    const loading = this.frameId && document.getElementById(`${this.frameId}_loading`)
    const content = this.frameId && document.getElementById(`${this.frameId}_content`)
    if (loading) loading.classList.remove("hidden")
    if (content) content.classList.add("hidden")
  }

  hideLoading() {
    const loading = this.frameId && document.getElementById(`${this.frameId}_loading`)
    const content = this.frameId && document.getElementById(`${this.frameId}_content`)
    if (loading) loading.classList.add("hidden")
    if (content) content.classList.remove("hidden")
  }

}
